import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";

import { Observable, map, of } from "rxjs";

import { VoucherBrandEnum, VoucherCreateModel, VoucherSkinEnum } from "@dert-models";
import { DropdownOption } from "../../components/ui-components/dropdown/dropdown.model";
import { TenderType } from "../model/tender-type.enum";
import {
  ICreateVoucherRequestParams,
  ICreateVoucherResult,
  IExecuteProcedureResult,
  IGetVoucherPdfResult,
  IPrintVoucherRequest,
  IPrintVoucherResult,
  IVoucherStatusRequest,
  IVoucherStatusResult,
  IVoucherStylesResponseData,
  ISetVoucherPaymentRequestParams,
  ISendVoucherRequestParams,
  ResultCode,
} from "../model/pos.api.model";
import { ProcedureNameEnum } from "src/app/enums/procedure-name.enum";
import { DbSchemeEnum } from "src/app/enums/db-scheme.enum";
import { SharedApiService } from "./sharedApi.service";
import { PopUpDialogInformalService } from "../confirms/popUpDialogInformal.service";
import { TranslateService } from "@ngx-translate/core";
import { PopUpInformalType } from "src/app/components/ui-components/pop-ups/pop-up-informal/pop-up-informal.model";
import { TransactionService } from "./transaction.service";
import { IBlobExportData } from "../model/sp-procedures-params.model";
import { IPayment } from "../model/get-trip-data.model";
import { environment } from "src/environments/environment";

@Injectable({
  providedIn: "root",
})
export class VoucherApiService {

  private _source = "PosNG"; // Hardcoded: just name of source system, always will be PosNG.
  private _sharedApiService: SharedApiService = inject(SharedApiService);
  private _popUpDialogInformalService: PopUpDialogInformalService = inject(PopUpDialogInformalService);
  private translate = inject(TranslateService);

  constructor(private http: HttpClient) { }


  public getVoucherVersionOptions(brand: VoucherBrandEnum, source: string): Observable<any> {
    return this.getVoucherTypes(brand, source)
      .pipe(map(res => {

        let options: DropdownOption[] = [];
        options.push({ label: 'webPos.Voucher.VoucherSkinDefault', value: VoucherSkinEnum.Default });

        res?.voucherTypes?.forEach(option => {
          switch (option.voucherStyle) {

            case VoucherSkinEnum.Beach:
              options.push({ label: 'webPos.Voucher.VoucherSkinBeach', value: VoucherSkinEnum.Beach });
              break;
            case VoucherSkinEnum.Christmas:
              options.push({ label: 'webPos.Voucher.VoucherSkinChristmas', value: VoucherSkinEnum.Christmas });
              break;
            case VoucherSkinEnum.Ski:
              options.push({ label: 'webPos.Voucher.VoucherSkinSki', value: VoucherSkinEnum.Ski });
              break;
            case VoucherSkinEnum.Valentine:
              options.push({ label: 'webPos.Voucher.VoucherSkinValentine', value: VoucherSkinEnum.Valentine });
              break;
            default:
              options.push({ label: option.voucherStyle, value: option.voucherStyle });
              break;
          }
        });

        return options;
      }));

  }


  public getVoucherTypes(brand: VoucherBrandEnum, source: string): Observable<any> {
    // localhost test: const url = `http://192.168.0.198:4202/api/dert/voucher/brand/${brand}/source/${source}/voucher-types`; 
    const url = `api/dert/voucher/brand/${brand}/source/${source}/voucher-types`;

    return this.http.get(url);
  }

  // Get Voucher List
  public getVoucherList(bnr: string, source: string): Observable<any> {
    // localhost test: const url = `http://192.168.0.198:4202/api/dert/voucher/bnr/${bnr}/source/${source}/voucher-list`;
    const url = `api/dert/voucher/bnr/${bnr}/source/${source}/voucher-list`;
    return this.http.get(url);
  }

  public printVoucher(request: IPrintVoucherRequest): Observable<IPrintVoucherResult> {
    const url = `${environment.apiAddress}/api/dert/PrintVoucher`; /* DO NOT CHANGE THIS ENDPOINT */
    return this.http.post<IPrintVoucherResult>(url, request);
  }

  // Send Voucher
  public postSendVoucher(data: any): Observable<any> {
    // localhost test: const url = `http://192.168.0.198:4202/api/dert/voucher/send-voucher`;
    const url = `api/dert/voucher/send-voucher`;
    return this.http.post(url, data);
  }

  /**
   * Creates voucher, also creates payment if possible (not for CARD payment)
   */
   async createVoucher(
    blob: IBlobExportData,
    payment: IPayment,
    options?: { createPaymentRecord?: boolean },
  ): Promise<ICreateVoucherResult> {
      const data: ICreateVoucherRequestParams = {
        Brand: VoucherBrandEnum.Dert,
        Source: this._source,
        VoucherCurrency: payment.Currency,
        VoucherPrice: payment.Amount,
        SendEmailToOwner: blob.sendEmail,
        VoucherOwner: {
          OwnerName: blob.customerName,
          OwnerSurname: blob.customerSurname,
          OwnerEmail: blob.customerEmailAddress,
          OwnerPhone: blob.customerPhoneNumber,
          OwnerCustomerId: null,
        },
        VoucherCustomerDetails: {
          Street: blob.customerStreet ?? null,
          City: blob.customerCity ?? null,
          Country: blob.customerCountry ?? null,
          ZipCode: blob.customerZipCode ?? null,
          CIN: blob.customerCIN ?? null,
          TIN: blob.customerTIN ?? null,
        },
        VoucherVersion: blob.visualStyle,
        VoucherPDF: {
          note: blob.description,
        },
        IdVoucherPayment: blob.idVoucherPayment ?? null,
        VoucherType: blob.voucherType,
        Payment: payment,
        ...(options?.createPaymentRecord !== undefined
          ? { CreatePaymentRecord: options.createPaymentRecord }
          : {}),
      };
  
      const request = {
        ProcedureName: ProcedureNameEnum.CreateNewVoucher,
        DbScheme: DbSchemeEnum.Dert,
        Parameters: {
          ...data,
        },
      };
  
      const response: IExecuteProcedureResult<ICreateVoucherResult> = await this._sharedApiService.executeProcedure(request);
      console.log("Create void voucher response:", response);
      return {
        ...response.data,
        voucherCode: response.data?.voucherCode ?? response.data?.voucherNumber,
      };
    }

    /**
     * Gets voucher PDF as base64 string
     * @param headerUID - The HeaderUID from the voucher creation result
     * @returns Promise with base64 encoded PDF content
     */
    async getVoucherPdf(headerUID: string, HeaderID: number, posHeaderUID?: string): Promise<IGetVoucherPdfResult | null> {
      const request = {
        ProcedureName: ProcedureNameEnum.GetVoucherPdf,
        DbScheme: DbSchemeEnum.Dert,
        Parameters: {
          HeaderUID: headerUID,
          HeaderID: HeaderID,
          PosHeaderUID: posHeaderUID ?? null,
        },
      };

      const response: IExecuteProcedureResult<IGetVoucherPdfResult> = 
        await this._sharedApiService.executeProcedure(request);
      
      if (!response || response.result !== ResultCode.OK || !response.data) {
        await this._popUpDialogInformalService.confirm({
          title: this.translate.instant("webPos.Errors.Error"),
          message: this.translate.instant("webPos.Errors.ServerError"),
          type: PopUpInformalType.Error,
        });
        return null;
      }

      return response.data;
    }

    

    async loadVoucherStyles(): Promise<any> {
   
        const brand = "dertouristik"; // Hardcoded: for now always will be Dertouristik until we will need brand selection
        const request = {
          Parameters: {
            Brand: brand,
            Source: this._source,
          },
          ProcedureName: ProcedureNameEnum.GetVoucherStyles,
          DbScheme: DbSchemeEnum.Dert,
        };
        const response: IExecuteProcedureResult<IVoucherStylesResponseData> =
          await this._sharedApiService.executeProcedure(request);
    
        if (
          !response ||
          response.result !== ResultCode.OK ||
          !response.data.success
        ) {
          await this._popUpDialogInformalService.confirm({
            title: this.translate.instant("webPos.Errors.Error"),
            message: this.translate.instant("webPos.Errors.VoucherStylesLoadError"),
            type: PopUpInformalType.Error,
          });
          return null;
        }
        return response.data;
      }

  async getVoucherStatus(bnr: number, source: string): Promise<IVoucherStatusResult | null> {
    const request = {
      Parameters: {
        bnr,
        source,
      } as IVoucherStatusRequest,
      ProcedureName: ProcedureNameEnum.VoucherStatus,
      DbScheme: DbSchemeEnum.Dert,
    };

    const response: IExecuteProcedureResult<IVoucherStatusResult> =
      await this._sharedApiService.executeProcedure(request);

    if (!response || response.result !== ResultCode.OK || !response.data) {
      await this._popUpDialogInformalService.confirm({
        title: this.translate.instant("webPos.Errors.Error"),
        message: this.translate.instant("webPos.Errors.ServerError"),
        type: PopUpInformalType.Error,
      });
      return null;
    }

    return response.data;
  }

  async activateVoucher(voucherCode: string, idVoucherPayment: string): Promise<boolean> {
    const request = {
      Parameters: {
        code: voucherCode,
        idVoucherPayment,
      },
      ProcedureName: ProcedureNameEnum.ActivateDertVoucher,
      DbScheme: DbSchemeEnum.Dert,
    };  

    // File result
    const response: IExecuteProcedureResult<any> = 
      await this._sharedApiService.executeProcedure(request);

    if (!response || response.result !== ResultCode.OK || !response.data) {
      await this._popUpDialogInformalService.confirm({
        title: this.translate.instant("webPos.Errors.Error"),
        message: this.translate.instant("webPos.Errors.ServerError"),
        type: PopUpInformalType.Error,
      });
      return false;
    }

    return response.data;
  }

  async setVoucherPayment(code: string, idVoucherPayment: string): Promise<boolean> {
    const request = {
      Parameters: {
        code,
        idVoucherPayment,
      } as ISetVoucherPaymentRequestParams,
      ProcedureName: ProcedureNameEnum.SetVoucherPayment,
      DbScheme: DbSchemeEnum.Dert,
    };

    const response: IExecuteProcedureResult<any> = await this._sharedApiService.executeProcedure(request);
    if (!response || response.result !== ResultCode.OK || !response.data) {
      await this._popUpDialogInformalService.confirm({
        title: this.translate.instant("webPos.Errors.Error"),
        message: this.translate.instant("webPos.Errors.ServerError"),
        type: PopUpInformalType.Error,
      });
      return false;
    }

    return true;
  }

  async sendVoucher(requestParams: ISendVoucherRequestParams): Promise<boolean> {
    const request = {
      Parameters: requestParams,
      ProcedureName: ProcedureNameEnum.SendVoucher,
      DbScheme: DbSchemeEnum.Dert,
    };

    const response: IExecuteProcedureResult<any> = await this._sharedApiService.executeProcedure(request);
    if (!response || response.result !== ResultCode.OK || !response.data) {
      await this._popUpDialogInformalService.confirm({
        title: this.translate.instant("webPos.Errors.Error"),
        message: this.translate.instant("webPos.Errors.ServerError"),
        type: PopUpInformalType.Error,
      });
      return false;
    }

    return true;
  }
}
